import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { ChartBar, Users, CurrencyDollar, Eye, Globe } from "@phosphor-icons/react";
import api from "../lib/api";

export default function AnalyticsPage() {
  const [stats, setStats] = useState({});
  const [pageViews, setPageViews] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [detailed, setDetailed] = useState(null);

  useEffect(() => {
    const fetchAll = () => {
      api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {});
      api.get("/analytics/pageviews").then(r => setPageViews(r.data)).catch(() => {});
      api.get("/exchange-rate").then(r => setExchangeRate(r.data?.usd_to_ngn)).catch(() => {});
      api.get("/analytics/detailed").then(r => setDetailed(r.data)).catch(() => {});
    };
    fetchAll();
    // Near-real-time refresh — re-poll every 30 seconds while the page is open.
    const t = setInterval(fetchAll, 30_000);
    return () => clearInterval(t);
  }, []);

  const fmtNGN = (usd) => exchangeRate ? `NGN ${Math.round(usd * exchangeRate).toLocaleString()}` : "";

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-testid="analytics-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-1">Reports</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-950 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Analytics & Reports</h1>
          </div>
          <Link to="/dashboard" className="text-sm text-slate-600 hover:text-slate-900">&larr; Back to Dashboard</Link>
        </div>

        {/* Enrollment Analytics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border border-slate-200 rounded-none shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2"><Users size={14} /> Enrollment Status</h3>
              <div className="space-y-3">
                {[
                  { label: "Active", value: stats.enrollment_breakdown?.active || 0, color: "bg-emerald-500" },
                  { label: "Pending Payment", value: stats.enrollment_breakdown?.pending || 0, color: "bg-amber-500" },
                  { label: "Completed", value: stats.enrollment_breakdown?.completed || 0, color: "bg-blue-500" },
                ].map(item => {
                  const total = stats.total_enrollments || 1;
                  const pct = Math.round((item.value / total) * 100);
                  return (
                    <div key={item.label}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-600">{item.label}</span>
                        <span className="font-semibold text-slate-900">{item.value} ({pct}%)</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-none overflow-hidden">
                        <div className={`h-full ${item.color} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 rounded-none shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2"><CurrencyDollar size={14} /> Revenue</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-800" /><span className="text-sm text-slate-600">Bank Transfer</span></div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-slate-900">${(stats.revenue_by_type?.transfer || 0).toLocaleString()}</span>
                    {exchangeRate && <p className="text-[10px] text-slate-400">{fmtNGN(stats.revenue_by_type?.transfer || 0)}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 bg-emerald-500" /><span className="text-sm text-slate-600">Stripe</span></div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-slate-900">${(stats.revenue_by_type?.stripe || 0).toLocaleString()}</span>
                    {exchangeRate && <p className="text-[10px] text-slate-400">{fmtNGN(stats.revenue_by_type?.stripe || 0)}</p>}
                  </div>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">Total</span>
                  <div className="text-right">
                    <span className="text-lg font-bold text-slate-950">${(stats.total_revenue || 0).toLocaleString()}</span>
                    {exchangeRate && <p className="text-xs text-slate-500">{fmtNGN(stats.total_revenue || 0)}</p>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 rounded-none shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2"><ChartBar size={14} /> Key Metrics</h3>
              <div className="space-y-3 text-sm">
                <Link to="/admin/users" className="flex justify-between hover:bg-slate-50 p-1 -m-1 transition-colors cursor-pointer"><span className="text-slate-600">Total Students</span><span className="font-semibold text-blue-800">{stats.total_students || 0}</span></Link>
                <Link to="/admin/users" className="flex justify-between hover:bg-slate-50 p-1 -m-1 transition-colors cursor-pointer"><span className="text-slate-600">Instructors</span><span className="font-semibold text-blue-800">{stats.total_instructors || 0}</span></Link>
                <Link to="/programmes" className="flex justify-between hover:bg-slate-50 p-1 -m-1 transition-colors cursor-pointer"><span className="text-slate-600">Programmes</span><span className="font-semibold text-blue-800">{stats.total_programmes || 0}</span></Link>
                <Link to="/courses" className="flex justify-between hover:bg-slate-50 p-1 -m-1 transition-colors cursor-pointer"><span className="text-slate-600">Courses</span><span className="font-semibold text-blue-800">{stats.total_courses || 0}</span></Link>
                <Link to="/certificates" className="flex justify-between hover:bg-slate-50 p-1 -m-1 transition-colors cursor-pointer"><span className="text-slate-600">Certificates</span><span className="font-semibold text-blue-800">{stats.total_certificates || 0}</span></Link>
                <Link to="/meetings" className="flex justify-between hover:bg-slate-50 p-1 -m-1 transition-colors cursor-pointer"><span className="text-slate-600">Meetings</span><span className="font-semibold text-blue-800">{stats.total_meetings || 0}</span></Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Analytics */}
        {detailed && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4">Signup & Enrollment</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Total Users</span><span className="font-semibold">{detailed.total_users}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Students</span><span className="font-semibold">{detailed.students}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">New Signups (7 days)</span><span className="font-semibold text-blue-800">{detailed.signups_last_7_days}</span></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-slate-600">Active Enrollments</span><span className="font-semibold text-emerald-700">{detailed.active_enrollments}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Pending Payment</span><span className="font-semibold text-amber-600">{detailed.pending_enrollments}</span></div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4">Popular Courses</h3>
                {detailed.popular_courses?.length > 0 ? (
                  <div className="space-y-2">
                    {detailed.popular_courses.map((c, i) => (
                      <Link key={i} to={`/courses`} className="flex justify-between items-center text-sm hover:bg-slate-50 p-1 -m-1 transition-colors">
                        <span className="text-slate-700 truncate flex-1 mr-2">{c.title}</span>
                        <Badge className="rounded-none text-[10px]">{c.student_count} students</Badge>
                      </Link>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-400">No course data yet</p>}
              </CardContent>
            </Card>

            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4">Free Course Engagement</h3>
                {detailed.free_courses_popular?.length > 0 ? (
                  <div className="space-y-2">
                    {detailed.free_courses_popular.map((c, i) => (
                      <div key={i} className="flex justify-between items-center text-sm">
                        <span className="text-slate-700 truncate flex-1 mr-2">{c.title}</span>
                        <Badge variant="outline" className="rounded-none text-[10px]">{c.student_count} taking</Badge>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-400">No free course data yet</p>}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Site Visitors */}
        {pageViews && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2"><Eye size={14} /> Site Visitors</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-800/5 border border-blue-800/10 p-4">
                    <p className="text-2xl font-bold text-blue-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{pageViews.total_views || 0}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Total Page Views</p>
                  </div>
                  <div className="bg-emerald-600/5 border border-emerald-600/10 p-4">
                    <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "'Outfit', sans-serif" }}>{pageViews.today_views || 0}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Today's Views</p>
                  </div>
                </div>
                {pageViews.daily?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2 font-medium">Last 7 Days</p>
                    <div className="flex items-end gap-1 h-20">
                      {pageViews.daily.map((d, i) => {
                        const max = Math.max(...pageViews.daily.map(x => x.count), 1);
                        const h = Math.max((d.count / max) * 100, 4);
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-[9px] text-slate-500 font-medium">{d.count}</span>
                            <div className="w-full bg-blue-800 rounded-sm transition-all" style={{ height: `${h}%` }} />
                            <span className="text-[8px] text-slate-400">{d.date.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2"><Globe size={14} /> Top Pages</h3>
                {pageViews.by_page?.length > 0 ? (
                  <div className="space-y-3">
                    {pageViews.by_page.slice(0, 10).map((p, i) => {
                      const max = pageViews.by_page[0]?.count || 1;
                      const pct = Math.round((p.count / max) * 100);
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-slate-600 font-mono text-xs truncate max-w-[200px]">{p.page}</span>
                            <span className="font-semibold text-slate-900 text-xs">{p.count}</span>
                          </div>
                          <div className="w-full h-1.5 bg-slate-100 rounded-none overflow-hidden">
                            <div className="h-full bg-blue-800 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No page view data yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
